import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, type LucideIcon } from 'lucide-react';
import { TopicLearnPosts } from '@/components/learn/TopicLearnPosts';
import { ProblemTable } from '@/components/problems/ProblemTable';
import { problemsApi, type ProblemQuery } from '@/api/endpoints';
import { mapProblemSummary } from '@/api/mappers';
import type { ApiProblemTopic, ProblemSummaryResponse } from '@/api/types';
import { DIFFICULTIES, difficultyPath } from '@/data/difficulties';
import { TOPICS, topicPath } from '@/data/topics';
import { useLocalized } from '@/hooks/useLocalized';
import { useSeo } from '@/hooks/useSeo';
import { useInitialData } from '@/ssr/initialData';
import { sortByDifficulty } from '@/lib/related';
import { pageSeoTitle } from '@/lib/seoText';
import type { LocalizedText } from '@/types';
import type { JsonLdNode } from '@/lib/structuredData';

interface ProblemCollectionProps {
  /** Kanonik yo'l: /topics/join, /problems/difficulty/easy */
  path: string;
  title: LocalizedText;
  desc: LocalizedText;
  icon?: LucideIcon;
  /** <title> uchun nom (default: title.uz) — "JOIN'lar — SQL masalalar" */
  seoName?: string;
  /** API filtri */
  query: Pick<ProblemQuery, 'topic' | 'difficulty'>;
  /** Breadcrumb'dagi ota sahifa (JSON-LD BreadcrumbList bilan bir xil) */
  parent: { to: string; labelKey: string };
  /** Sahifaga xos JSON-LD (lib/structuredData) */
  jsonLd: JsonLdNode[];
  /** Mavzu sahifasi — shu mavzudagi "O'rganish" darsliklari bloki */
  learnTopic?: ApiProblemTopic;
}

/**
 * Masalalar to'plami sahifasi (mavzu yoki qiyinlik): tavsif + barcha masalalar
 * (oson → qiyin) + boshqa to'plamlarga havolalar. Prerender qilinadi; bo'sh
 * to'plam noindex (sitemap'da ham yo'q).
 */
export function ProblemCollection({ path, title, desc, icon: Icon, seoName, query, parent, jsonLd, learnTopic }: ProblemCollectionProps) {
  const { t } = useTranslation();
  const { l, lang } = useLocalized();

  const initial = useInitialData();
  const initialItems = initial?.collection && initial.path === path ? initial.collection : null;

  const [items, setItems] = useState<ProblemSummaryResponse[] | null>(initialItems);
  const [error, setError] = useState(false);
  const loadedKey = useRef<string | null>(initialItems ? `${path}|${initial?.lang}` : null);

  useEffect(() => {
    const key = `${path}|${lang}`;
    if (loadedKey.current === key) return;
    let cancelled = false;
    setError(false);
    // Boshqa to'plamga o'tildi — eski ro'yxat ko'rinib turmasin (til almashsa qoladi)
    if (!loadedKey.current?.startsWith(`${path}|`)) setItems(null);
    problemsApi
      .list({ ...query, size: 100, lang })
      .then((res) => {
        if (cancelled) return;
        loadedKey.current = key;
        setItems(sortByDifficulty(res.data.content));
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
    // query — path dan kelib chiqadi
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, lang]);

  useSeo({
    title: pageSeoTitle(seoName ?? title.uz),
    description: desc.uz,
    path,
    // Bo'sh to'plam — yupqa kontent, indekslanmasin
    noindex: items !== null && items.length === 0,
    jsonLd,
  });

  return (
    <div className="mx-auto max-w-8xl px-4 py-8 sm:px-6 lg:px-8">
      <nav aria-label={t('problem.breadcrumb')} className="mb-4 text-sm">
        <ol className="flex flex-wrap items-center gap-1.5 text-content-secondary">
          <li>
            <Link to="/" className="transition-colors hover:text-content">
              {t('nav.home')}
            </Link>
          </li>
          <li aria-hidden>
            <ChevronRight className="h-3.5 w-3.5" />
          </li>
          <li>
            <Link to={parent.to} className="transition-colors hover:text-content">
              {t(parent.labelKey)}
            </Link>
          </li>
          <li aria-hidden>
            <ChevronRight className="h-3.5 w-3.5" />
          </li>
          <li aria-current="page" className="text-content">
            {l(title)}
          </li>
        </ol>
      </nav>

      <div className="mb-8">
        <h1 className="flex items-center gap-2 text-2xl font-bold sm:text-3xl">
          {Icon && <Icon className="h-6 w-6 shrink-0 text-brand-600" aria-hidden />}
          {l(title)}
        </h1>
        <p className="mt-2 max-w-3xl text-content-secondary">{l(desc)}</p>
        {items && (
          <p className="mt-1 text-sm text-content-muted">{t('common.problemsCount', { count: items.length })}</p>
        )}
      </div>

      {error ? (
        <div className="rounded-lg border border-line bg-elevated py-16 text-center text-content-secondary">
          {t('problems.loadError')}
        </div>
      ) : items === null ? (
        <div className="rounded-lg border border-line bg-elevated py-16 text-center text-content-secondary">
          {t('common.loading')}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-line bg-elevated py-16 text-center text-content-secondary">
          {t('topic.empty')}
        </div>
      ) : (
        <ProblemTable problems={items.map((p) => mapProblemSummary(p))} />
      )}

      {learnTopic && <TopicLearnPosts key={learnTopic} topic={learnTopic} path={path} />}

      <CollectionLinks current={path} />
    </div>
  );
}

/** Boshqa mavzular va qiyinlik darajalari — ichki havolalar. */
export function CollectionLinks({ current }: { current?: string }) {
  const { t } = useTranslation();
  const { l } = useLocalized();
  return (
    <div className="mt-12 grid gap-8 md:grid-cols-2">
      <section>
        <h2 className="mb-4 text-lg font-semibold">{t('topic.byDifficulty')}</h2>
        <ul className="flex flex-wrap gap-2">
          {DIFFICULTIES.filter((d) => difficultyPath(d.api) !== current).map((d) => (
            <li key={d.api}>
              <Link to={difficultyPath(d.api)} className="tag transition-colors hover:text-brand-600">
                {l(d.title)}
              </Link>
            </li>
          ))}
        </ul>
      </section>
      <section>
        <h2 className="mb-4 text-lg font-semibold">{t('topic.otherTopics')}</h2>
        <ul className="flex flex-wrap gap-2">
          {TOPICS.filter((tp) => topicPath(tp.slug) !== current).map((tp) => (
            <li key={tp.slug}>
              <Link to={topicPath(tp.slug)} className="tag gap-1 transition-colors hover:text-brand-600">
                <tp.icon className="h-3.5 w-3.5" aria-hidden />
                {l(tp.title)}
              </Link>
            </li>
          ))}
          {current && (
            <li>
              <Link to="/problems" className="tag transition-colors hover:text-brand-600">
                {t('topic.allProblems')}
              </Link>
            </li>
          )}
        </ul>
      </section>
    </div>
  );
}
